import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePDIContext } from '../context/PDIContext';
import { CHECKLIST_SECTIONS } from '../data/checklistData';
import { AlertOctagon, ChevronRight, ShieldX } from 'lucide-react';

// Items that should block delivery if failed
const DELIVERY_BLOCKERS = [
  // VIN/Identification
  'vd-1', // VIN Number Match
  'vd-2', // Engine Number Match
  'vd-3', // Chassis Number
  'vd-6', // Odometer Reading (if too high)
  
  // Exterior Critical
  'ext-2', // Paint Color Consistency (indicates repaint/accident)
  'ext-5', // Windshield & Glass
  
  // Interior Safety
  'int-3', // Seat Belts
  'int-8', // Handbrake / Parking Brake
  'int-14', // Floor Mats (pedal interference)
  
  // Electrical Safety
  'elec-2', // Instrument Cluster warnings
  'elec-9', // AC (major comfort issue)
  'elec-12', // Horn
  
  // Engine Critical
  'eng-1', // Engine Oil Level
  'eng-2', // Coolant Level
  'eng-3', // Brake Fluid Level
  'eng-7', // No Visible Leaks
  
  // Tyre Safety
  'tyre-5', // Sidewall Condition
  'tyre-8', // Spare Tyre
  'tyre-9', // Jack & Tools
  
  // Underbody
  'under-5', // No Fluid Leaks
  
  // Documents Critical
  'doc-1', // Invoice
  'doc-2', // Insurance
  'doc-3', // Warranty Card
  'doc-7', // Form 21 & 22
  'doc-9', // All Key Sets
  
  // Test Drive Critical
  'td-1', // Engine Start
  'td-4', // Gear Shifts
  'td-6', // Braking
  'td-8', // Straight Line Tracking
  'td-10', // No Warning Lights
];

const DeliveryBlockerBanner = ({ compact = false }) => {
  const navigate = useNavigate();
  const { itemStatuses } = usePDIContext();

  const blockedItems = useMemo(() => {
    const blocked = [];
    
    CHECKLIST_SECTIONS.forEach(section => {
      section.items.forEach(item => {
        const status = itemStatuses[item.id];
        if (DELIVERY_BLOCKERS.includes(item.id) && status === 'fail') {
          blocked.push({
            ...item,
            sectionTitle: section.title,
          });
        }
      });
    });
    
    return blocked;
  }, [itemStatuses]);

  if (blockedItems.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div 
        className="bg-gradient-to-r from-danger-600 to-danger-700 text-white px-4 py-3.5 flex items-center justify-between cursor-pointer border-b-4 border-danger-800"
        onClick={() => navigate('/summary')}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <ShieldX className="w-4 h-4" />
          </div>
          <div>
            <p className="font-bold text-sm">
              {blockedItems.length} Delivery Blocker{blockedItems.length > 1 ? 's' : ''}
            </p>
            <p className="text-danger-100 text-xs">
              Do NOT accept delivery
            </p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-white/70" />
      </div>
    );
  }

  return (
    <div className="bg-danger-50 border-2 border-danger-300 rounded-2xl p-5 mb-6 animate-fade-in">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-danger-600 flex items-center justify-center flex-shrink-0">
          <AlertOctagon className="w-6 h-6 text-white" />
        </div>
        
        <div className="flex-1">
          <h3 className="font-display font-bold text-danger-800 text-lg">
            ❌ Do NOT Accept Delivery
          </h3>
          <p className="text-sm text-danger-700 mt-1">
            The following critical items have failed and must be resolved before you accept your car:
          </p>
          
          <div className="mt-4 space-y-2">
            {blockedItems.map(item => (
              <div 
                key={item.id}
                className="bg-white rounded-lg p-3 border border-danger-200 flex items-start gap-3"
              >
                <ShieldX className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-danger-800">{item.title}</p>
                  <p className="text-xs text-danger-600">{item.sectionTitle}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-danger-100 rounded-xl">
            <p className="text-sm font-medium text-danger-800">
              What you should do:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-danger-700">
              <li>• Get written acknowledgment for each issue</li>
              <li>• Request immediate resolution before signing</li>
              <li>• If critical issues persist, reject delivery</li>
              <li>• Contact manufacturer customer care if needed</li>
            </ul>
          </div>

          <button
            onClick={() => navigate('/summary')}
            className="mt-4 w-full bg-danger-600 text-white font-semibold py-3 rounded-xl btn-touch flex items-center justify-center gap-2"
          >
            View Full Report
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export { DELIVERY_BLOCKERS };
export default DeliveryBlockerBanner;
